import { useState } from 'react';
import { ExternalLink, BookmarkPlus, FolderPlus, X, ChevronDown, MapPin, Clock, Wrench, Info, ShieldCheck, BookOpenCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { RESULTS } from '@/constants/testIds';
import {
  fmtCurrency,
  fmtDistance,
  fmtDriveTime,
  opportunityMeta,
  strategyLabel,
  timeAgo,
} from '@/lib/format';
import { toast } from 'sonner';
import { SaveToCollectionModal } from '@/components/SaveToCollectionModal';

export const ListingCard = ({ listing }) => {
  const [expanded, setExpanded] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveCollectionOpen, setSaveCollectionOpen] = useState(false);
  const op = listing.opportunity || {};
  const opMeta = opportunityMeta(op.label);
  const analysis = listing.analysis || {};

  const save = async () => {
    try {
      await api.post(`/saved/${listing.id}`);
      setSaved(true);
      toast.success('Saved to Vault');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const open = () => window.open(listing.external_url, '_blank', 'noopener,noreferrer');

  return (
    <article
      data-testid={RESULTS.listingCard}
      className="zita-card p-5 flex flex-col gap-4 zita-fade-up"
    >
      <div className="flex flex-col sm:flex-row gap-4">
        {listing.photos && listing.photos[0] ? (
          <img
            src={listing.photos[0]}
            alt=""
            className="w-full sm:w-32 h-40 sm:h-32 rounded-lg object-cover border border-zita-taupe"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full sm:w-32 h-40 sm:h-32 rounded-lg bg-zita-sand border border-zita-taupe flex items-center justify-center text-zita-muted text-xs">
            No photo
          </div>
        )}
        <div className="flex-1 min-w-0">
          {listing.result_kind === 'preview' && (
            <div
              data-testid="listing-preview-badge"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full mb-1.5"
              title="This site blocked direct access, but Ari found a public search preview. Open the original to view full details."
            >
              Preview result
            </div>
          )}
          {listing.result_kind === 'sign_in_required' && (
            <div
              data-testid="listing-signin-badge"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold zita-blue-text bg-[hsl(var(--zita-blue-soft))] px-2 py-0.5 rounded-full mb-1.5"
              title="Sign in to this source on your device to see the full listing."
            >
              Sign-in required
            </div>
          )}
          <div className="flex flex-wrap items-start gap-2 justify-between">
            <h3 className="font-serif text-xl leading-tight text-zita-espresso">{listing.title}</h3>
            {op.label && (
              <span
                data-testid={RESULTS.opportunityBadge}
                className={`text-xs px-3 py-1 rounded-full font-semibold ${opMeta.klass}`}
              >
                {opMeta.text}{op.opportunity_score ? ` · ${op.opportunity_score}` : ''}
              </span>
            )}
          </div>
          {listing.result_kind === 'preview' && (
            <p className="text-[11px] zita-ink-muted italic mt-1">
              This site blocked direct access, but Ari found a public search preview. Open the original for full details.
            </p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-zita-mocha">
            <span className="font-semibold text-zita-espresso">
              {listing.is_free ? 'Free' : fmtCurrency(listing.price)}
            </span>
            {listing.location_text && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={12} /> {listing.location_text}
              </span>
            )}
            {listing.distance_miles != null && (
              <span>{fmtDistance(listing.distance_miles)}</span>
            )}
            {fmtDriveTime(listing.drive_time_minutes) && (
              <span>{fmtDriveTime(listing.drive_time_minutes)}</span>
            )}
            {listing.posted_at && (
              <span className="inline-flex items-center gap-1">
                <Clock size={12} /> {timeAgo(listing.posted_at)}
              </span>
            )}
            <span className="text-zita-muted">· {listing.source_name}</span>
          </div>
          {analysis.ai_summary && (
            <p className="mt-3 text-sm text-zita-espresso/85">{analysis.ai_summary}</p>
          )}
          {op.recommended_path && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-zita-muted">Exit strategy:</span>
              <span
                className="ari-chip px-2 py-0.5 rounded-full text-xs font-medium"
                data-testid="listing-exit-strategy"
              >
                {strategyLabel(op.recommended_path)}
              </span>
            </div>
          )}
          {listing.qualification_verdict === 'possible_match' && (
            <div
              className="mt-3 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800"
              data-testid="listing-possible-match-badge"
              title={listing.qualification_reason || 'Possible match — review to confirm.'}
            >
              <Info size={12} /> Possible match
            </div>
          )}

          {/* Iteration 37 §Advanced Research Layer — per-listing badges.
              Show corroboration status when it's meaningful ("supported" /
              "contradicted"), and show page-inspection outcome when it
              differed from the first-pass verdict (a badge would signal an
              upgrade, "reviewed" a downgrade or an unchanged inspection). */}
          {listing.research_notes?.corroboration?.status === 'supported' && (
            <div
              className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800"
              data-testid="listing-corroboration-badge"
              title={listing.research_notes.corroboration.note || 'Corroborated by another independent public source.'}
            >
              <ShieldCheck size={12} /> Corroborated ({(listing.research_notes.corroboration.supported_by || []).length})
            </div>
          )}
          {listing.research_notes?.page_inspection_verdict === 'match' && (
            <div
              className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-800"
              data-testid="listing-page-inspected-badge"
              title={listing.research_notes.page_inspection_reason || 'Ari opened the page and confirmed this match.'}
            >
              <BookOpenCheck size={12} /> Page inspected
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-zita-taupe/60">
        <button
          type="button"
          onClick={open}
          data-testid={RESULTS.openListing}
          className="zita-btn-primary text-sm inline-flex items-center gap-2"
        >
          Open Listing <ExternalLink size={14} />
        </button>
        <button
          type="button"
          onClick={save}
          data-testid={RESULTS.saveListing}
          disabled={saved}
          className="zita-btn-secondary text-sm inline-flex items-center gap-2"
        >
          <BookmarkPlus size={14} /> {saved ? 'Saved' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setSaveCollectionOpen(true)}
          data-testid="results-save-to-collection"
          className="zita-btn-secondary text-sm inline-flex items-center gap-2"
        >
          <FolderPlus size={14} /> Collection
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={RESULTS.analysisToggle}
          className="ml-auto text-sm text-zita-cocoa hover:underline inline-flex items-center gap-1"
        >
          {expanded ? 'Hide' : 'Show'} analysis <ChevronDown size={14} className={expanded ? 'rotate-180' : ''} />
        </button>
      </div>

      {expanded && (
        <div className="zita-card-flat p-4 space-y-3 text-sm">
          {op.opportunity_score !== undefined && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <Stat label="Score" value={op.opportunity_score} />
              <Stat label="As-is" value={rng(op.estimated_market_value_low, op.estimated_market_value_high)} />
              <Stat label="Repaired" value={rng(op.estimated_repaired_value_low, op.estimated_repaired_value_high)} />
              <Stat label="Scrap" value={op.estimated_scrap_value != null ? fmtCurrency(op.estimated_scrap_value) : '—'} />
            </div>
          )}
          {op.paths?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-zita-muted mb-2">
                Exit strategies
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {op.paths.map((p, i) => (
                  <div
                    key={i}
                    data-testid={RESULTS.opportunityCard}
                    className={`p-3 rounded-lg border text-xs ${
                      p.strategy === op.recommended_path
                        ? 'border-zita-cocoa bg-zita-sand'
                        : 'border-zita-taupe bg-white/40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-zita-espresso">{strategyLabel(p.strategy)}</span>
                      <span className="text-zita-muted">{p.confidence}</span>
                    </div>
                    <div className="mt-1 text-zita-mocha">
                      Net {rng(p.estimated_net_low, p.estimated_net_high)}
                      {p.estimated_time_hours ? ` · ${p.estimated_time_hours}h` : ''}
                    </div>
                    {p.rationale && <div className="mt-1 text-zita-muted">{p.rationale}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(analysis.facts?.length || analysis.estimates?.length || analysis.unknowns?.length) > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <List title="Facts" items={analysis.facts} accent="text-zita-espresso" />
              <List title="Estimates" items={analysis.estimates} accent="text-zita-cocoa" />
              <List title="Unknowns" items={analysis.unknowns} accent="text-zita-muted" />
            </div>
          )}
          {analysis.repair_risk && (
            <div className="text-xs text-zita-mocha inline-flex items-center gap-1">
              <Wrench size={12} /> Repair risk: <span className="font-semibold">{analysis.repair_risk}</span>
              {analysis.ai_confidence && <> · confidence <span className="font-semibold">{analysis.ai_confidence}</span></>}
            </div>
          )}
          <div className="text-[11px] text-zita-muted flex items-start gap-1">
            <Info size={11} className="mt-0.5" /> Estimates are AI-generated. Verify before purchase.
          </div>
        </div>
      )}
      {saveCollectionOpen && (
        <SaveToCollectionModal
          listing={listing}
          scanId={listing.scan_id}
          onClose={() => setSaveCollectionOpen(false)}
          onSaved={() => setSaveCollectionOpen(false)}
        />
      )}
    </article>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-zita-taupe/50 bg-white/40 p-2">
    <div className="text-[10px] uppercase tracking-wider text-zita-muted">{label}</div>
    <div className="font-semibold text-zita-espresso text-sm">{value}</div>
  </div>
);

const List = ({ title, items, accent }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zita-muted mb-1">{title}</div>
      <ul className={`space-y-1 text-xs ${accent}`}>
        {items.slice(0, 4).map((it, i) => (
          <li key={i}>• {it}</li>
        ))}
      </ul>
    </div>
  );
};

const rng = (a, b) => {
  if (a == null && b == null) return '—';
  if (a != null && b != null) return `${fmtCurrency(a)}–${fmtCurrency(b)}`;
  return fmtCurrency(a ?? b);
};
